import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import classNames from "classnames";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FinancialAccount } from "@common/types/financialAccount";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { AccountPickerItem } from "@components/financialAccount/AccountPickerItem";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useChangeFinancialAccountMutation } from "@mutations/financialDocument";
import { getDocumentLockedTooltip } from "../../lib";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const FinancialAccountCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        FinancialDocumentListItem,
        FinancialAccount | null
    >) => {
        const document = row.original;
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const accounts = useFinancialAccounts();
        const lockedTooltip = getDocumentLockedTooltip(
            document,
            document.integrationAccount,
        );
        const accountsById = useMemo(
            () =>
                new Map<number, FinancialAccount>(
                    accounts.map((a: FinancialAccount) => [a.id, a]),
                ),
            [accounts],
        );
        const account = document.financialAccount
            ? (accountsById.get(document.financialAccount.id) ??
              document.financialAccount)
            : null;
        const changeAccount = useChangeFinancialAccountMutation();
        const accountItems = useMemo(
            () =>
                accounts.map((menuAccount: FinancialAccount) => (
                    <AccountPickerItem
                        key={menuAccount.id}
                        account={menuAccount}
                    />
                )),
            [accounts],
        );

        const connection = account
            ? getLeadingAccountConnection(account)
            : null;

        const accountCardContent = useMemo(
            () =>
                account ? (
                    <AccountCard
                        account={account}
                        connection={connection ?? undefined}
                        onlyNumber
                        displayNameAsTooltip
                        showType={false}
                        iconSize="sm"
                    />
                ) : null,
            [account, connection],
        );

        if (lockedTooltip || isTableReadonly) {
            return (
                <ViewTableCell>
                    {account ? (
                        <EditableCellContent readonly tooltip={lockedTooltip}>
                            {accountCardContent}
                        </EditableCellContent>
                    ) : null}
                </ViewTableCell>
            );
        }

        const handleSelect = (
            selectedAccountId: FinancialAccount["id"] | null,
        ) => {
            const selectedAccount = selectedAccountId
                ? (accountsById.get(selectedAccountId) ?? null)
                : null;
            changeAccount.mutate({
                financialDocument: document,
                financialAccount: selectedAccount,
            });
        };

        return (
            <ViewTableCell>
                <Picker.Select
                    value={account?.id ?? null}
                    onValueChange={handleSelect}
                >
                    <span
                        className={classNames({
                            [viewTableStyles.showOnHover]: !account,
                            [viewTableStyles.forceShow]: !!account,
                        })}
                    >
                        <Picker.CellContent
                            addText="Select account"
                            hideAddIcon
                            value={accountCardContent ?? undefined}
                            aria-label={account?.name ?? undefined}
                        />
                    </span>
                    <Picker.Popup>
                        <Picker.List>
                            {accounts.length === 0 ? (
                                <Picker.Empty>
                                    No accounts available
                                </Picker.Empty>
                            ) : (
                                <>
                                    {accountItems}
                                    {account && (
                                        <>
                                            <Picker.Separator />
                                            <Picker.Item
                                                onSelect={() =>
                                                    handleSelect(null)
                                                }
                                            >
                                                Remove account
                                            </Picker.Item>
                                        </>
                                    )}
                                </>
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
FinancialAccountCell.displayName = "FinancialAccountCell";
